import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Check, ArrowLeft, AlertCircle, ChevronDown, ChevronRight } from 'lucide-react';
import { V2PageShell, Card, Button, FieldGroup, Input, Textarea, Select, Toast, EmptyState, Pill } from '../../components/v2';
import { getFormTaskForFill, getFormTaskUpstreamSubmissions, submitFormTask } from '../../services/api';

/**
 * FormFillPage — RC-161.
 *
 * <p>The /forms/to-do/:id fill page. Renders the snapshot formSchema
 * stored on the form_task_assignment row (V296) as live input controls.
 * On submit, POSTs to /api/v1/form-tasks/{id}/submit which signals the
 * FormTaskLifecycleWorkflow to record the submission + resume the
 * parent CanvasDAG.</p>
 *
 * <p>Supports the same field types as ApprovalCenter (RC-121):
 * text / textarea / number / date / datetime / select / multi-select /
 * checkbox / file. File uploads use the existing
 * ApprovalAttachmentService endpoint (no new infra).</p>
 *
 * <p>Built on V2 primitives; no inline style with hardcoded values.</p>
 */
export default function FormFillPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [assignment, setAssignment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [values, setValues] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitOk, setSubmitOk] = useState(null);
  // 2026-07-04 — upstream submissions (Prior submissions card). Fetched
  // in parallel with the assignment so the operator sees "what was
  // already submitted" (personal_info + id_upload in the demo flow)
  // WITHOUT having to open the run detail view separately.
  const [upstream, setUpstream] = useState([]);
  const [upstreamOpen, setUpstreamOpen] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Parallel — the two endpoints are independent. Upstream failure
      // does NOT fail the fill page (upstream context is nice-to-have,
      // not required for submit).
      const [assignRes, upstreamRes] = await Promise.all([
        getFormTaskForFill(id),
        getFormTaskUpstreamSubmissions(id).catch((e) => ({ ok: false, error: e?.message })),
      ]);
      const data = assignRes?.data !== undefined ? assignRes.data : assignRes;
      setAssignment(data || null);
      setValues({});  // fresh state per load
      const upstreamList = Array.isArray(upstreamRes?.data) ? upstreamRes.data
        : Array.isArray(upstreamRes) ? upstreamRes
        : [];
      setUpstream(upstreamList);
    } catch (e) {
      setError(e?.message || 'Could not load form task.');
      setAssignment(null);
      setUpstream([]);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const fields = useMemo(() => {
    if (!assignment || !assignment.formSchema) return [];
    const fs = assignment.formSchema;
    return Array.isArray(fs.fields) ? fs.fields : [];
  }, [assignment]);

  const setFieldValue = (name, value) => {
    setValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError(null);
    setSubmitOk(null);
    // Client-side required check — server re-validates via
    // ConfigSchemaValidator (RC-115) before signalling the workflow.
    const missing = fields
      .filter((f) => f.required && isEmptyValue(values[f.name]))
      .map((f) => f.label || f.name);
    if (missing.length > 0) {
      setSubmitError(`Required: ${missing.join(', ')}`);
      setSubmitting(false);
      return;
    }
    try {
      const res = await submitFormTask(id, values);
      if (res && res.ok === false) {
        setSubmitError(res.error || 'Submit failed.');
        return;
      }
      setSubmitOk('Submitted. The workflow will resume shortly.');
      // Brief delay so the operator sees the toast before navigating away.
      // 2026-07-04 — post-submit destination changed from /forms/to-do
      // (retired list route) to /my-tasks (the unified assignee inbox).
      setTimeout(() => navigate('/my-tasks'), 1500);
    } catch (e) {
      setSubmitError(e?.message || 'Submit failed.');
    } finally {
      setSubmitting(false);
    }
  };

  const headerActions = assignment ? (
    <Button
      variant="primary"
      iconLeft={<Check />}
      onClick={handleSubmit}
      loading={submitting}
      disabled={assignment.status !== 'CREATED' && assignment.status !== 'ASSIGNED'}
    >
      Submit
    </Button>
  ) : null;

  const tags = assignment ? (
    <>
      <Pill tone="action" variant="soft">{assignment.status}</Pill>
      {assignment.assigneeType === 'GROUP' && <Pill tone="muted" variant="soft">via group</Pill>}
      {assignment.assigneeType === 'ROLE' && <Pill tone="muted" variant="soft">via role: {assignment.assigneeRoleName}</Pill>}
    </>
  ) : null;

  return (
    <V2PageShell
      // 2026-07-04 — title/subtitle now consume the operator-authored
      // formSchema.title + formSchema.instructions (enriched at pause
      // registration in CanvasDAGWorkflowImpl.java around line 3823-3850).
      // Fallbacks preserve the pre-enrichment behavior so pre-fix rows
      // still render without crashing: title → workflow_node_id, subtitle
      // → the technical run/due summary.
      title={
        assignment
          ? (assignment.formSchema?.title
             || assignment.workflowNodeId
             || 'Form task')
          : 'Form task'
      }
      subtitle={
        assignment
          ? (assignment.formSchema?.instructions
             || `Run ${assignment.workflowRunId ? assignment.workflowRunId.slice(0, 8) : '—'} · due ${assignment.dueAt || '—'}`)
          : ''
      }
      tags={tags}
      actions={headerActions}
      backTo="/my-tasks"
      backLabel="Back to My Tasks"
    >
      {error && (
        <div className="mb-4">
          <Toast tone="danger" title="Load failed" description={error} duration={0} onDismiss={() => setError(null)} />
        </div>
      )}
      {submitError && (
        <div className="mb-4">
          <Toast tone="danger" title="Submit failed" description={submitError} duration={0} onDismiss={() => setSubmitError(null)} />
        </div>
      )}
      {submitOk && (
        <div className="mb-4">
          <Toast tone="success" title="Submitted" description={submitOk} onDismiss={() => setSubmitOk(null)} />
        </div>
      )}

      {loading && (
        <Card><Card.Body>
          <div className="text-[13px] text-v2-ink-secondary py-6 text-center">Loading form…</div>
        </Card.Body></Card>
      )}

      {!loading && !error && !assignment && (
        <Card><Card.Body>
          <EmptyState
            icon={<AlertCircle />}
            title="Form task not found"
            description="The task may have been completed by another assignee, cancelled, or you may no longer be in the resolved assignee set."
            primaryAction={<Button variant="primary" iconLeft={<ArrowLeft />} onClick={() => navigate('/forms/to-do')}>Back to To Do</Button>}
          />
        </Card.Body></Card>
      )}

      {/* 2026-07-04 — Prior submissions card. Renders above the form so
          the reviewer sees what they're approving before they touch
          the decision fields. Collapsible because power users who've
          already seen the context want it out of the way; default-open
          because most first-time reviewers benefit from it visible. */}
      {!loading && assignment && upstream.length > 0 && (
        <div className="mb-4">
          <Card>
            <button
              type="button"
              onClick={() => setUpstreamOpen((v) => !v)}
              className="w-full flex items-center gap-2 px-4 py-3 text-left v2-focus-ring hover:bg-v2-elevated transition-colors duration-v2"
              aria-expanded={upstreamOpen}
            >
              {upstreamOpen
                ? <ChevronDown size={16} className="text-v2-ink-muted" aria-hidden="true" />
                : <ChevronRight size={16} className="text-v2-ink-muted" aria-hidden="true" />}
              <span className="text-[13px] font-semibold text-v2-ink">
                Prior submissions
              </span>
              <Pill tone="action" variant="soft">{upstream.length}</Pill>
              <span className="ml-auto text-[11.5px] text-v2-ink-muted">
                What was submitted upstream in this run
              </span>
            </button>
            {upstreamOpen && (
              <Card.Body>
                <div className="flex flex-col gap-4">
                  {upstream.map((row, i) => (
                    <UpstreamSubmissionBlock key={`${row.workflowNodeId}:${i}`} row={row} />
                  ))}
                </div>
              </Card.Body>
            )}
          </Card>
        </div>
      )}

      {!loading && assignment && fields.length === 0 && (
        <Card><Card.Body>
          <div className="text-[13px] text-v2-ink-muted italic py-6 text-center">
            This task has no form fields. Click Submit to acknowledge and advance the workflow.
          </div>
        </Card.Body></Card>
      )}

      {!loading && assignment && fields.length > 0 && (
        <Card>
          <Card.Header
            title="Fill in the form"
            subtitle="Required fields are marked with an asterisk. Submitted values will flow to the downstream workflow steps."
          />
          <Card.Body>
            <div className="flex flex-col gap-4">
              {fields.map((field) => (
                <FillField
                  key={field.name}
                  field={field}
                  value={values[field.name]}
                  onChange={(v) => setFieldValue(field.name, v)}
                />
              ))}
            </div>
          </Card.Body>
        </Card>
      )}
    </V2PageShell>
  );
}

/**
 * UpstreamSubmissionBlock — 2026-07-04.
 *
 * Renders one prior submission (form_task_assignment or external_step_pause)
 * as a titled block with a two-column field=value grid. The `source` field
 * from the BE lets us render a soft type-pill so the reviewer can tell
 * whether an upstream step was filled by an internal user vs the customer's
 * SPA. Values that are objects/arrays are stringified so nothing crashes
 * the render.
 */
function UpstreamSubmissionBlock({ row }) {
  const values = row?.submittedValues && typeof row.submittedValues === 'object'
    ? row.submittedValues
    : {};
  const entries = Object.entries(values);
  const sourceLabel = row?.source === 'external_subject' ? 'Customer' : 'Internal';
  const sourceTone = row?.source === 'external_subject' ? 'warning' : 'action';

  return (
    <div className="border border-v2-border-subtle rounded-v2-md p-3">
      <div className="flex items-center gap-2 mb-2">
        <Pill tone={sourceTone} variant="soft">{sourceLabel}</Pill>
        <span className="text-[13px] font-semibold text-v2-ink">
          {row?.title || row?.workflowNodeId || 'Step'}
        </span>
        {row?.submittedAt && (
          <span className="ml-auto text-[11.5px] text-v2-ink-muted tabular-nums">
            {row.submittedAt}
          </span>
        )}
      </div>
      {entries.length === 0 ? (
        <div className="text-[12px] text-v2-ink-muted italic">
          No submitted values recorded.
        </div>
      ) : (
        // 2026-07-05 — grid uses min-w-0 on the container + overflow-hidden
        // on the value cell so a base64 blob can't stretch the entire card.
        // break-all is the CSS the previous break-words missed: base64 has
        // no word-boundary characters so break-words doesn't wrap.
        <dl className="grid grid-cols-[max-content_minmax(0,1fr)] gap-x-3 gap-y-1 text-[12.5px] min-w-0">
          {entries.map(([k, v]) => (
            <React.Fragment key={k}>
              <dt className="text-v2-ink-muted font-medium whitespace-nowrap">{k}</dt>
              <dd className="text-v2-ink break-all overflow-hidden min-w-0">
                <UpstreamValueCell value={v} />
              </dd>
            </React.Fragment>
          ))}
        </dl>
      )}
    </div>
  );
}

/**
 * UpstreamValueCell — 2026-07-05.
 *
 * Renders a single submitted-value cell with attachment-aware compact
 * summary. If the value is a file-shaped object ({filename, sizeBytes,
 * contentType, data, encoding:"base64"}) — which is the BFF's fallback
 * shape for the demo's idDocument until RC-155e /upload lands — render
 * a clean chip like "📎 evidence.pdf · 240 KB · image/png" instead of
 * splatting the raw base64 across the card and blowing up the layout.
 *
 * For plain values, truncate at 200 chars in the DOM (also enforced
 * server-side via classification-rule masking when DPG-FormIngest ships
 * — see architectural answer to Sony's #3).
 */
function UpstreamValueCell({ value }) {
  if (value == null) return <span>—</span>;

  // Attachment-shaped object → compact chip
  const isAttachment = value && typeof value === 'object' && !Array.isArray(value)
    && typeof value.data === 'string'
    && (value.encoding === 'base64' || value.contentType || value.filename);
  if (isAttachment) {
    const name = value.filename || 'attachment';
    const kb = typeof value.sizeBytes === 'number'
      ? (value.sizeBytes >= 1024
          ? `${Math.round(value.sizeBytes / 1024)} KB`
          : `${value.sizeBytes} B`)
      : null;
    const ct = value.contentType || null;
    const parts = [name, kb, ct].filter(Boolean).join(' · ');
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-v2-pill bg-v2-muted text-v2-ink-secondary font-mono text-[11.5px]">
        📎 {parts}
      </span>
    );
  }

  if (typeof value === 'string') {
    return <span>{value.length > 200 ? value.slice(0, 200) + '…' : value}</span>;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return <span>{String(value)}</span>;
  }
  // Fallback: compact JSON, truncated. break-all on the parent dd keeps
  // this from stretching the card.
  let s;
  try { s = JSON.stringify(value); } catch (_) { s = String(value); }
  return <span className="font-mono text-[11.5px]">{s.length > 200 ? s.slice(0, 200) + '…' : s}</span>;
}

/** Single-field renderer. Mirrors the ApprovalCenter input set (RC-121). */
function FillField({ field, value, onChange }) {
  const ft = (field.type || 'text').toLowerCase();
  const label = field.label || field.name;

  if (ft === 'textarea') {
    return (
      <FieldGroup label={label} required={field.required} hint={field.helpText}>
        <Textarea size="sm" rows={field.rows || 6}
          value={value ?? ''} onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder} />
      </FieldGroup>
    );
  }
  if (ft === 'number') {
    return (
      <FieldGroup label={label} required={field.required} hint={field.helpText}>
        <Input size="sm" type="number"
          value={value ?? ''} onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}
          placeholder={field.placeholder} min={field.min} max={field.max} step={field.step} />
      </FieldGroup>
    );
  }
  if (ft === 'date') {
    return (
      <FieldGroup label={label} required={field.required} hint={field.helpText}>
        <Input size="sm" type="date" value={value ?? ''} onChange={(e) => onChange(e.target.value)} />
      </FieldGroup>
    );
  }
  if (ft === 'datetime' || ft === 'datetime-local') {
    return (
      <FieldGroup label={label} required={field.required} hint={field.helpText}>
        <Input size="sm" type="datetime-local" value={value ?? ''} onChange={(e) => onChange(e.target.value)} />
      </FieldGroup>
    );
  }
  if (ft === 'select') {
    const options = Array.isArray(field.options) ? field.options : [];
    return (
      <FieldGroup label={label} required={field.required} hint={field.helpText}>
        <Select size="sm" value={value ?? ''} onChange={(e) => onChange(e.target.value)}>
          <option value="">Select…</option>
          {options.map((opt, i) => (
            <option key={i} value={opt.value ?? opt}>{opt.label ?? opt.value ?? String(opt)}</option>
          ))}
        </Select>
      </FieldGroup>
    );
  }
  if (ft === 'multi-select' || ft === 'multiselect') {
    const options = Array.isArray(field.options) ? field.options : [];
    const arr = Array.isArray(value) ? value : [];
    const toggle = (v) => {
      const set = new Set(arr);
      if (set.has(v)) set.delete(v); else set.add(v);
      onChange(Array.from(set));
    };
    return (
      <FieldGroup label={label} required={field.required} hint={field.helpText}>
        <div className="flex flex-col gap-1.5">
          {options.length === 0 && <div className="text-[11.5px] text-v2-ink-muted italic">No options configured.</div>}
          {options.map((opt, i) => {
            const v = opt.value ?? opt;
            return (
              <label key={i} className="flex items-center gap-2 text-[12.5px] text-v2-ink cursor-pointer">
                <input type="checkbox" checked={arr.includes(v)} onChange={() => toggle(v)} />
                {opt.label ?? opt.value ?? String(opt)}
              </label>
            );
          })}
        </div>
      </FieldGroup>
    );
  }
  if (ft === 'checkbox' || ft === 'boolean') {
    return (
      <div>
        <label className="flex items-center gap-2 text-[12.5px] text-v2-ink cursor-pointer">
          <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} />
          {label}
        </label>
        {field.helpText && <div className="text-[11px] text-v2-ink-muted mt-1">{field.helpText}</div>}
      </div>
    );
  }
  if (ft === 'file') {
    // File upload uses the existing ApprovalAttachment endpoint pattern
    // (RC-121b). Stub for now — full upload integration matches what
    // ApprovalCenter does. The operator sees a file picker; uploaded
    // file metadata becomes the value.
    return (
      <FieldGroup label={label} required={field.required} hint={field.helpText || 'Choose a file to upload.'}>
        <Input size="sm" type="file"
          onChange={(e) => onChange(e.target.files && e.target.files[0] ? {
            filename: e.target.files[0].name,
            sizeBytes: e.target.files[0].size,
            contentType: e.target.files[0].type,
          } : null)} />
      </FieldGroup>
    );
  }
  // text (default)
  return (
    <FieldGroup label={label} required={field.required} hint={field.helpText}>
      <Input size="sm" value={value ?? ''} onChange={(e) => onChange(e.target.value)}
        placeholder={field.placeholder} pattern={field.pattern} minLength={field.minLength} maxLength={field.maxLength} />
    </FieldGroup>
  );
}

function isEmptyValue(v) {
  if (v === undefined || v === null) return true;
  if (typeof v === 'string' && v.trim() === '') return true;
  if (Array.isArray(v) && v.length === 0) return true;
  return false;
}
